import { NavigationStep, NavigationHistory, PlaybackState } from '@dukkan/shared-types/navigation';

/**
 * Service for extracting and working with frame numbers
 */
export class FrameSequenceService {
  /**
   * Extract frame number from filename (e.g., "frame_0123.jpg" → 123)
   */
  static extractFrameNumber(filename: string): number | null {
    if (!filename || typeof filename !== 'string') {
      console.warn('[FrameSequenceService] Invalid filename:', filename);
      return null;
    }

    const match = filename.match(/frame_(\d{4})\.jpg$/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Find next frame in sequence (current + 1)
   */
  static getNextInSequence(cameras: any[], currentIndex: number): number | null {
    if (!Array.isArray(cameras) || cameras.length === 0) {
      console.warn('[FrameSequenceService] Invalid cameras array');
      return null;
    }

    if (currentIndex < 0 || currentIndex >= cameras.length) {
      console.warn('[FrameSequenceService] Invalid currentIndex:', currentIndex);
      return null;
    }

    const currentCamera = cameras[currentIndex];
    if (!currentCamera?.frame_file) {
      console.warn('[FrameSequenceService] No frame_file in current camera');
      return null;
    }

    const currentFrameNum = this.extractFrameNumber(currentCamera.frame_file);
    if (currentFrameNum === null) return null;

    // Find next frame (currentFrameNum + 1)
    for (let i = 0; i < cameras.length; i++) {
      if (!cameras[i]?.frame_file) continue;
      const frameNum = this.extractFrameNumber(cameras[i].frame_file);
      if (frameNum === currentFrameNum + 1) return i;
    }

    return null; // No next frame found
  }

  /**
   * Get random frame within range
   */
  static getRandomInRange(cameras: any[], currentIndex: number, range: number = 20): number | null {
    if (!Array.isArray(cameras) || cameras.length === 0) return null;
    if (currentIndex < 0 || currentIndex >= cameras.length) return null;

    const min = Math.max(0, currentIndex - range);
    const max = Math.min(cameras.length - 1, currentIndex + range);

    if (min >= max) return null;

    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * (max - min + 1)) + min;
    } while (randomIndex === currentIndex); // Avoid same frame

    return randomIndex;
  }
}

/**
 * Service for managing navigation history
 */
export class NavigationHistoryService {
  /**
   * Record a new navigation step
   */
  static recordStep(
    history: NavigationHistory,
    step: NavigationStep,
    maxSize: number = 100
  ): NavigationHistory {
    // Input validation
    if (!step || typeof step.startFrame !== 'number' || step.startFrame < 0 ||
        typeof step.endFrame !== 'number' || step.endFrame < 0) {
      console.warn('[NavigationHistoryService] Invalid step:', step);
      return history;
    }

    // Skip recording if no navigation occurred (start === end with same trigger as previous)
    const lastStep = history.steps[history.steps.length - 1];
    if (lastStep && step.startFrame === step.endFrame &&
        step.startFrame === lastStep.endFrame && step.trigger === lastStep.trigger) {
      return history; // No-op navigation, skip recording
    }

    if (!history || !Array.isArray(history.steps)) {
      console.warn('[NavigationHistoryService] Invalid history:', history);
      return history;
    }

    // Truncate future history if we're not at the end
    const newSteps = [...history.steps.slice(0, history.currentIndex + 1), step];

    // Enforce max size (FIFO)
    if (newSteps.length > maxSize) {
      newSteps.shift(); // Remove oldest
    }

    return {
      steps: newSteps,
      currentIndex: newSteps.length - 1
    };
  }

  /**
   * Navigate backward in history
   */
  static goBack(history: NavigationHistory): { history: NavigationHistory; targetIndex: number | null } {
    if (!history || history.currentIndex <= 0) {
      return { history, targetIndex: null };
    }

    const newIndex = history.currentIndex - 1;
    const targetStep = history.steps[newIndex];

    if (!targetStep) {
      console.warn('[NavigationHistoryService] No step at index:', newIndex);
      return { history, targetIndex: null };
    }

    return {
      history: { ...history, currentIndex: newIndex },
      targetIndex: targetStep.endFrame // Jump to where user finished
    };
  }

  /**
   * Navigate forward in history
   */
  static goForward(history: NavigationHistory): { history: NavigationHistory; targetIndex: number | null } {
    if (!history || history.currentIndex >= history.steps.length - 1) {
      return { history, targetIndex: null };
    }

    const newIndex = history.currentIndex + 1;
    const targetStep = history.steps[newIndex];

    if (!targetStep) {
      console.warn('[NavigationHistoryService] No step at index:', newIndex);
      return { history, targetIndex: null };
    }

    return {
      history: { ...history, currentIndex: newIndex },
      targetIndex: targetStep.endFrame // Jump to where user finished
    };
  }
}

/**
 * Service for playback logic
 */
export class PlaybackService {
  private static readonly DEFAULT_FPS = 2; // Hardcoded for now

  /**
   * Calculate interval for playback (ms)
   */
  static calculateInterval(speed: number = 1): number {
    const fps = this.DEFAULT_FPS;
    const baseInterval = 1000 / fps; // ms per frame
    return Math.max(50, baseInterval / speed); // Min 50ms to prevent too fast
  }

  /**
   * Get next camera for sequence playback
   */
  static getNextForSequence(cameras: any[], currentIndex: number): number | null {
    return FrameSequenceService.getNextInSequence(cameras, currentIndex);
  }

  /**
   * Get next camera for random playback
   */
  static getNextForRandom(cameras: any[], currentIndex: number, range: number = 20): number | null {
    return FrameSequenceService.getRandomInRange(cameras, currentIndex, range);
  }

  /**
   * Validate playback state
   */
  static isValidPlaybackState(state: PlaybackState): boolean {
    return (
      typeof state === 'object' &&
      typeof state.isPlaying === 'boolean' &&
      (state.mode === null || state.mode === 'sequence' || state.mode === 'random') &&
      typeof state.speed === 'number' && state.speed > 0 &&
      (state.intervalId === null || typeof state.intervalId === 'number')
    );
  }
}